<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Audit Trail</h5>
        <p class="page-sub">Immutable record of every visitor action — cannot be edited or deleted</p>
      </div>
    </div>

    <div class="filter-bar">
      <input v-model="filters.visit_id" class="filter-input" placeholder="Filter by Visit ID…" @input="debounceLoad" />
      <select v-model="filters.action" class="filter-select" @change="load(1)">
        <option value="">All Actions</option>
        <option value="pre_registered">Pre-Registered</option>
        <option value="checked_in">Checked In</option>
        <option value="walked_in">Walk-In</option>
        <option value="checked_out">Checked Out</option>
        <option value="cancelled">Cancelled</option>
        <option value="no_show">No Show</option>
        <option value="approved">Approved</option>
        <option value="denied">Denied</option>
        <option value="approval_requested">Approval Requested</option>
        <option value="marked_safe">Marked Safe</option>
        <option value="overstay_notified">Overstay Alert</option>
        <option value="dsr_erased">DSR Erased</option>
      </select>
      <input v-model="filters.from" type="date" class="filter-input" @change="load(1)" />
      <input v-model="filters.to"   type="date" class="filter-input" @change="load(1)" />
    </div>

    <div v-if="loading" class="sk-table"><div v-for="n in 8" :key="n" class="sk-row"></div></div>

    <div v-else-if="!rows.data?.length && !rows.length" class="empty-state">
      <font-awesome-icon icon="history" class="empty-icon" />
      <p class="empty-title">No audit entries</p>
      <p class="empty-sub">Events will appear here as visitors are checked in, out, approved and more.</p>
    </div>

    <div v-else class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>Action</th>
            <th>Visit ID</th>
            <th>Actor</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in (rows.data ?? rows)" :key="r.id">
            <td class="td-mono">{{ formatDt(r.created_at) }}</td>
            <td>
              <span class="action-badge" :class="`action-badge--${actionClass(r.action)}`">
                <font-awesome-icon :icon="actionIcon(r.action)" />
                {{ actionLabel(r.action) }}
              </span>
            </td>
            <td class="td-mono" style="font-size:11px;color:#7a90b0;">{{ r.visit_id?.slice(0, 8) }}…</td>
            <td class="td-muted">{{ r.actor_user_id ? r.actor_user_id.slice(0, 8) + '…' : 'System' }}</td>
            <td>
              <span v-if="r.meta && Object.keys(r.meta).length" class="meta-text">
                {{ metaSummary(r.meta) }}
              </span>
              <span v-else class="td-muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="rows.last_page > 1" class="pagination-bar">
        <button :disabled="page === 1" class="pg-btn" @click="load(page - 1)">‹ Prev</button>
        <span class="pg-info">Page {{ rows.current_page }} of {{ rows.last_page }}</span>
        <button :disabled="page === rows.last_page" class="pg-btn" @click="load(page + 1)">Next ›</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

const ACTION_MAP = {
  pre_registered:    { label: 'Pre-Registered', icon: 'calendar-plus',     cls: 'blue'   },
  checked_in:        { label: 'Checked In',      icon: 'sign-in-alt',       cls: 'green'  },
  walked_in:         { label: 'Walk-In',          icon: 'walking',           cls: 'green'  },
  checked_out:       { label: 'Checked Out',      icon: 'sign-out-alt',      cls: 'grey'   },
  cancelled:         { label: 'Cancelled',        icon: 'times-circle',      cls: 'grey'   },
  no_show:           { label: 'No Show',          icon: 'user-times',        cls: 'amber'  },
  approved:          { label: 'Approved',         icon: 'check-circle',      cls: 'green'  },
  denied:            { label: 'Denied',           icon: 'ban',               cls: 'red'    },
  approval_requested:{ label: 'Approval Req.',    icon: 'clock',             cls: 'amber'  },
  marked_safe:       { label: 'Marked Safe',      icon: 'shield-alt',        cls: 'green'  },
  overstay_notified: { label: 'Overstay Alert',   icon: 'exclamation-circle',cls: 'amber'  },
  dsr_erased:        { label: 'DSR Erased',       icon: 'trash-alt',         cls: 'red'    },
  reminder_sent:     { label: 'Reminder Sent',    icon: 'bell',              cls: 'blue'   },
}

export default {
  name: 'VisitorAuditTrail',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true, rows: [], page: 1,
      filters: { visit_id: '', action: '', from: '', to: '' },
      debounceTimer: null,
    }
  },
  created() { this.load() },
  methods: {
    debounceLoad() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => this.load(1), 400) },
    async load(p = 1) {
      this.loading = true; this.page = p
      const params = { page: p, per_page: 50 }
      if (this.filters.visit_id) params.visit_id = this.filters.visit_id
      if (this.filters.action)   params.action   = this.filters.action
      if (this.filters.from)     params.from     = this.filters.from
      if (this.filters.to)       params.to       = this.filters.to
      try {
        const r = await axios.get('visitors/audit', { params })
        this.rows = r.data.data ?? r.data
      } catch { this.toast.error('Failed to load audit trail.') }
      finally { this.loading = false }
    },
    actionLabel(a) { return ACTION_MAP[a]?.label ?? a },
    actionIcon(a)  { return ACTION_MAP[a]?.icon  ?? 'info-circle' },
    actionClass(a) { return ACTION_MAP[a]?.cls   ?? 'grey' },
    metaSummary(meta) {
      return Object.entries(meta)
        .slice(0, 3)
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
        .join(' · ')
    },
    formatDt(d) { return d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit' }) : '—' },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.filter-bar { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
.filter-input  { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; min-width:140px; font-family:inherit; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; background:#fff; cursor:pointer; font-family:inherit; }

.table-wrap { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table th { padding:10px 14px; background:#f4f7fb; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; text-align:left; border-bottom:1px solid #e4eaf2; }
.data-table td { padding:10px 14px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.data-table tr:last-child td { border-bottom:none; }
.td-mono  { font-family:monospace; font-size:12px; color:#4a5e78; }
.td-muted { color:#7a90b0; font-size:12px; }
.meta-text { font-size:11px; color:#4a5e78; font-family:monospace; max-width:280px; display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

.action-badge { display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:700; padding:4px 9px; border-radius:20px; }
.action-badge--green  { background:#dcfce7; color:#16a34a; }
.action-badge--blue   { background:#dbeafe; color:#1d4ed8; }
.action-badge--amber  { background:#fef3c7; color:#d97706; }
.action-badge--red    { background:#fee2e2; color:#dc2626; }
.action-badge--grey   { background:#f1f5f9; color:#64748b; }

.pagination-bar { display:flex; align-items:center; gap:10px; padding:12px 16px; border-top:1px solid #f0f4f8; }
.pg-btn  { padding:6px 14px; border:1px solid #e4eaf2; border-radius:8px; background:#fff; font-size:13px; cursor:pointer; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-info { font-size:13px; color:#7a90b0; flex:1; text-align:center; }

.empty-state { text-align:center; padding:60px 20px; }
.empty-icon  { font-size:48px; color:#dde5f0; margin-bottom:16px; }
.empty-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:48px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
